/**
 * Copyright (c) 2026 Briq-n-Braq LLC
 * All rights reserved.
 *
 * Proprietary and confidential software.
 * Unauthorized copying, modification, distribution, or use
 * of this software, in whole or in part, is strictly prohibited.
 *
 * KNX Supply Portal — portal.briq-n-braq.com
 * KNX Certified Professional | Partner No. 20248
 */

import React, { useState } from 'react';

const C = {
  navy:        '#0f1f3d',
  navyMid:     '#162d52',
  navyLight:   '#1e3a6e',
  navyBorder:  '#253d6e',
  orange:      '#F97316',
  orangeDim:   '#c2591b',
  white:       '#ffffff',
  slate:       '#94a3b8',
  slateLight:  '#cbd5e1',
  slateLighter:'#e2e8f0',
  red:         '#ef4444',
  amber:       '#eab308',
  green:       '#22c55e',
};

const SEVERITY_STYLES = {
  Critical: { bg: '#ef444418', border: '#ef444455', text: '#f87171' },
  High:     { bg: '#F9731618', border: '#F9731655', text: '#F97316' },
  Medium:   { bg: '#eab30818', border: '#eab30855', text: '#fbbf24' },
};

// ── Issue data ──────────────────────────────────────────────────────────────

const ISSUES = [
  {
    id: 'no-power',
    icon: '⚡',
    title: 'No Bus Power',
    severity: 'Critical',
    overview: 'All KNX TP devices are unresponsive because bus voltage is absent or out of specification. Correct bus voltage measures 29 V DC (±1 V) between the red (+) and black (−) bus terminals under load.',
    symptoms: [
      'Bus voltage measures 0 V or below 27 V DC at any point on the line',
      'Every device on the line is unresponsive — no switching, dimming, or feedback',
      'Programming LED on any device will not illuminate when the programming button is pressed',
      'ETS topology view shows all devices on the line as unreachable',
      'PSU current display (if fitted) reads 0 mA',
    ],
    causes: [
      'Mains supply to the PSU is disconnected, fused MCB has tripped, or fuse is blown',
      'Bus power supply (PSU) hardware has failed internally',
      'Short circuit on the KNX TP cable (e.g. cable crush, staple through insulation)',
      'Line choke missing, failed, or wired incorrectly — bypassing isolation',
      'Bus cable polarity reversed at one or more termination points',
    ],
    steps: [
      {
        title: 'Measure mains voltage at the PSU input terminals',
        detail: 'Using a multimeter set to AC voltage, probe the L and N terminals on the PSU. Expected reading: 230 V AC ±10% (207–253 V). If absent, trace back to the distribution board and check the MCB or fuse feeding the PSU circuit.',
      },
      {
        title: 'Inspect the PSU status LED',
        detail: 'Most KNX PSUs have a green status LED confirming bus power output. A dark or red LED indicates the PSU is not generating bus voltage. Consult the manufacturer datasheet for specific LED fault codes — some PSUs use flashing patterns to indicate overcurrent or short-circuit protection.',
      },
      {
        title: 'Measure DC bus voltage directly at the PSU output',
        detail: 'With the multimeter set to DC voltage, probe the KNX+ (red) and KNX− (black) output terminals on the PSU body — not downstream on the cable. Nominal reading is 29 V DC. Below 27 V under load suggests an overloaded or failing PSU.',
      },
      {
        title: 'Verify the line choke installation',
        detail: 'The line choke must be wired in series between the PSU output and the bus cable. Confirm: PSU (+) → Choke → Bus (+). A choke that has been bypassed, shorted, or installed in parallel will not isolate the PSU from bus signal noise and can cause abnormal bus behaviour.',
      },
      {
        title: 'Isolate a short circuit by disconnecting devices',
        detail: 'Disconnect all device bus terminals and re-measure voltage at the PSU. If voltage returns to 29 V, reconnect devices one branch at a time. When the faulty branch is reconnected, voltage will drop (typically below 24 V) and current will spike — this identifies the short-circuit branch.',
      },
      {
        title: 'Test cable insulation resistance',
        detail: 'With the PSU disconnected and all devices de-terminated, use a 500 V megohmmeter to test insulation resistance: KNX+ to KNX−, KNX+ to earth, KNX− to earth. All readings should exceed 1 MΩ. A reading below 100 kΩ indicates damaged cable insulation requiring replacement.',
      },
      {
        title: 'Replace the PSU if all external checks pass',
        detail: 'If mains voltage is confirmed correct and no external short circuit or wiring fault is found, the PSU itself has failed. Replace with a SELV-rated 29 V DC KNX PSU of sufficient current capacity. Choose 160 mA for up to 20 devices, 320 mA for up to 40 devices, or 640 mA for up to 64 devices per segment.',
      },
    ],
  },
  {
    id: 'device-not-responding',
    icon: '◉',
    title: 'Device Not Responding',
    severity: 'High',
    overview: 'A specific KNX device does not react to group telegrams or manual operation, while other devices on the same line function normally. The fault is isolated to one device or a small group of devices.',
    symptoms: [
      'Device physically unresponsive — no switching, dimming, or movement',
      'ETS bus monitor shows the command telegram sent but no ACK received from the device',
      'Device physical address shows as "not reachable" in ETS topology view',
      'Programming LED on the device will not light when the programming button is pressed',
      'Bus voltage is present and correct at all other devices on the line',
    ],
    causes: [
      'Device has no physical address (PA) assigned — it has never been commissioned',
      'Application program was not downloaded, or the download completed with errors',
      "Group address link is missing on the device's communication object",
      'Bus terminal connection loose, corroded, or reversed at the device',
      'Device is in lock / protection mode or has a firmware error',
      'Device hardware has failed',
    ],
    steps: [
      {
        title: 'Check whether the device has a physical address in ETS',
        detail: 'Open ETS Topology view. The device should appear at the correct position (e.g. 1.1.5). If it is missing or greyed out with no PA, the device has never been addressed. Press the programming button on the device — its LED should light red, confirming it is in programming mode and reachable.',
      },
      {
        title: 'Measure bus voltage at the device terminals',
        detail: 'With a multimeter set to DC voltage, probe the KNX+ (red) and KNX− (black) bus terminals directly on the device or its DIN-rail coupler. Reading should be 27–30 V DC. A reading below 24 V indicates insufficient bus power at that point — check for loose or corroded terminals on the cable run.',
      },
      {
        title: 'Re-download the full application to the device',
        detail: 'In ETS, right-click the device in the topology view and select "Download → All". This re-writes the PA, application program, and all parameters. Confirm the download completes with a green tick. If the download fails with a timeout, the device is not reachable — investigate bus power and cabling first.',
      },
      {
        title: 'Verify group address assignments on the device',
        detail: 'In ETS, open the device properties and navigate to the Group Objects tab. Confirm the relevant communication objects (COs) are linked to the correct group addresses. A CO with no GA assigned will never send or receive — it appears as an empty link field in the GA workspace.',
      },
      {
        title: 'Use ETS bus monitor to observe telegram flow',
        detail: "Open ETS Bus Monitor and trigger the control (e.g. press the push button). Observe whether the command telegram appears. Then check whether the target device's GA appears in the log with the correct value and datatype. An absent response from the device indicates it is not receiving or processing the telegram.",
      },
      {
        title: 'Attempt a factory reset of the device',
        detail: 'Most KNX devices can be reset to factory defaults by holding the programming button for 5–10 seconds until the LED flashes a reset pattern. Refer to the manufacturer datasheet for the exact procedure. After reset, re-download the application via ETS. Factory reset clears all PA, parameters, and GA links.',
      },
      {
        title: 'Check for device lock or protection mode',
        detail: 'Some KNX actuators have a physical lock switch or a software-configurable protection mode that prevents remote control. Check the device body for a hardware disable switch and verify ETS parameters for any "lock" or "manual operation only" setting. Download corrected parameters if enabled by mistake.',
      },
      {
        title: 'Replace the device if hardware failure is confirmed',
        detail: 'If the bus voltage is correct, the application downloads without error, GAs are properly assigned, and the device still does not respond, the device has an internal hardware fault. Replace with an identical or compatible model. Restore the PA, application, and GA links from the saved ETS project file.',
      },
    ],
  },
  {
    id: 'ets-comms',
    icon: '↔',
    title: 'ETS Cannot Communicate',
    severity: 'High',
    overview: 'ETS6 cannot establish a connection to the KNX bus through the USB or IP interface, making it impossible to address devices, download applications, or run bus diagnostics.',
    symptoms: [
      'ETS Interfaces panel shows "No interface found" or a persistent connection timeout',
      'Bus Monitor opens but displays no telegrams and shows a red "Disconnected" status',
      'Attempting a device download immediately fails with a communication error',
      'KNXnet/IP interface does not appear in the ETS interface scan results',
      'USB interface LED is off or the device is not listed in Windows Device Manager',
    ],
    causes: [
      'USB cable is loose, faulty, or plugged into a high-impedance USB hub',
      'Interface driver is not installed or has become corrupted in Windows',
      'IP interface and PC are on different subnets — IP layer cannot reach the interface',
      'Windows Firewall or network firewall is blocking UDP port 3671',
      'Bus power is absent — most USB/IP interfaces require 29 V DC on bus to operate',
      'ETS interface selection points to the wrong or a previously removed interface',
    ],
    steps: [
      {
        title: 'Check the USB cable and port',
        detail: 'Disconnect and reconnect the USB cable firmly. Try a different USB-A port on the PC — avoid USB hubs, which can cause intermittent enumeration failures. Try a replacement cable if available, as micro-USB and USB-B cables are common failure points.',
      },
      {
        title: 'Verify the driver in Windows Device Manager',
        detail: "Press Win+X → Device Manager. Expand \"Universal Serial Bus controllers\" or \"Ports (COM & LPT)\". The KNX USB interface should appear without a yellow warning triangle. If it shows an error, right-click → Update driver, or download the latest driver from the manufacturer's website (e.g. Siemens, Gira, ABB).",
      },
      {
        title: 'Scan for interfaces in ETS',
        detail: 'Go to ETS Settings → Interfaces. Click the scan/refresh icon and wait 15 seconds. ETS will probe both USB (via driver enumeration) and IP (via multicast discovery on UDP 3671). Confirm the expected interface appears in the list and shows a green "Connected" indicator.',
      },
      {
        title: 'Verify bus power is present',
        detail: 'USB and IP interfaces require bus power (29 V DC) to communicate with KNX devices. Measure voltage at the bus terminal of the interface. If bus power is absent, the interface hardware may respond to ETS on the PC side but will fail to communicate with bus devices.',
      },
      {
        title: 'Confirm IP interface and PC are on the same subnet',
        detail: "Check the IP interface's assigned address (visible in its web UI or printed label) and your PC's IP address (ipconfig in Command Prompt). Both must share the same subnet — e.g. PC: 192.168.1.50, Interface: 192.168.1.100 with a /24 mask. Different subnets will silently prevent UDP multicast discovery.",
      },
      {
        title: 'Ping the IP interface from the PC',
        detail: 'Open Command Prompt and run: ping <interface IP address>. A successful reply confirms basic IP connectivity. If ping fails, check network switch cabling, VLAN assignments, or any inter-VLAN routing rules that may be isolating the KNX device from the PC.',
      },
      {
        title: 'Temporarily disable Windows Firewall and retest',
        detail: 'ETS uses UDP multicast on port 3671 for KNXnet/IP discovery and communication. Open Windows Security → Firewall → Turn off for the Private network profile. Retry the ETS interface scan. If the interface now appears, add a permanent inbound/outbound rule for UDP port 3671 rather than leaving the firewall disabled.',
      },
      {
        title: 'Check for IP interface simultaneous connection limit',
        detail: 'KNXnet/IP interfaces in tunnelling mode typically support only 1–4 simultaneous client connections. If another PC, visualisation system, or app is already connected, ETS may not be able to establish a new tunnel. Disconnect other clients or switch the interface to routing mode for unrestricted simultaneous access.',
      },
      {
        title: 'Test with a known-good interface or a second PC',
        detail: "Swap to a different USB or IP interface if available, or try connecting with ETS installed on a different laptop. If the second setup connects successfully, the fault is specific to the original PC's driver or network configuration rather than the KNX bus or interface hardware.",
      },
    ],
  },
  {
    id: 'group-address',
    icon: '⬡',
    title: 'Group Address Not Working',
    severity: 'Medium',
    overview: 'A sensor or push button sends a telegram but the target actuator(s) do not respond. The bus is operational and both devices are individually reachable, but the functional link between them is broken.',
    symptoms: [
      'Pressing a push button produces no reaction from the linked lighting circuit or actuator',
      'ETS bus monitor shows the command telegram on the bus, but the actuator does not change state',
      'Only some devices in a group respond — others are silent',
      'A scene recall telegram is sent but one or more devices fail to reach the scene state',
      'Status feedback GA never updates even when the actuator does change state',
    ],
    causes: [
      'Sending communication object (CO) on the sensor is not linked to any group address',
      'Receiving CO on the actuator is linked to a different GA than the sensor CO',
      'Datatype mismatch — sender CO is 1-bit DPT 1.001 but receiver expects 1-byte DPT 5.001',
      'Line coupler filter table is blocking the GA from crossing between line segments',
      'Application download was not performed after the GA assignment was changed in ETS',
    ],
    steps: [
      {
        title: 'Open the ETS GA workspace and locate the group address',
        detail: 'In ETS, switch to the Group Addresses view. Find the GA in question and expand it. It should list at least one sender CO (push button output) and one or more receiver COs (actuator inputs). An empty GA or a GA with only receivers and no sender will never transmit a telegram.',
      },
      {
        title: 'Verify the sending CO is linked to the correct GA',
        detail: 'Click the push-button device in the ETS topology, open its Group Objects tab, and confirm the relevant output CO (e.g. "Switch Object") shows the intended GA in the "Group Addresses" column. If blank, drag the CO onto the target GA in the GA workspace or use the link dialog.',
      },
      {
        title: 'Verify the receiving CO on the actuator is linked',
        detail: 'Click the actuator device in ETS topology and open its Group Objects tab. Confirm the relevant input CO (e.g. "Channel A — Switch") shows the same GA as the sensor. A CO with no GA or a different GA will silently ignore incoming telegrams on the expected address.',
      },
      {
        title: 'Check that sender and receiver CO datatypes match',
        detail: "In the Group Objects tab, the \"Data Type\" column shows each CO's DPT. A push-button switch output is DPT 1.001 (1-bit). A switching actuator input must also be DPT 1.001. Mismatched DPTs (e.g. a 1-bit sender linked to a 1-byte receiver) cause the actuator to ignore or misinterpret the value.",
      },
      {
        title: 'Use ETS bus monitor to confirm telegram delivery',
        detail: "Open Bus Monitor, then trigger the push button. A well-formed write telegram to the correct GA should appear in the log — e.g. \"L_Data_Ind 1/1/5 DPT1 On\". If the telegram is visible but the actuator still does not respond, the fault is in the actuator's CO configuration or its application download. If no telegram appears, the sensor CO is not linked.",
      },
      {
        title: 'Check the line coupler filter table if devices are on different lines',
        detail: "If the sensor and actuator are on different KNX line segments (separated by a line coupler), the coupler's filter table must permit the GA to pass. In ETS, select the line coupler device and open its Filter Table. Confirm the relevant GA range is set to \"Route\" (pass-through) rather than \"Block\".",
      },
      {
        title: 'Download updated configuration to all affected devices',
        detail: 'After any GA change in ETS, the updated configuration must be downloaded to affected devices before it takes effect. Right-click each modified device in the ETS topology and select "Download → All". Do not skip the sensor device — both sender and receiver need the updated GA table.',
      },
      {
        title: 'Test with ETS group monitor',
        detail: 'Use ETS Group Monitor (not just Bus Monitor) to write a test value directly to the GA — right-click the GA and choose "Write Value". This bypasses the sensor entirely and tests whether the actuator alone responds. If the actuator reacts to a direct Group Monitor write but not to the sensor, the sensor CO configuration is the fault.',
      },
      {
        title: 'Check for duplicate GA assignments causing conflicts',
        detail: 'Search the ETS project for any other COs sharing the same GA. A second sender CO (e.g. from a logic block or timer) sending a conflicting value can override the push button output immediately after it fires, making the circuit appear unresponsive. Reassign or remove the conflicting link.',
      },
    ],
  },
  {
    id: 'line-coupler',
    icon: '⇄',
    title: 'Line Coupler Issues',
    severity: 'Medium',
    overview: 'KNX telegrams are not passing correctly between line segments — devices on a sub-line do not respond to commands from the main line, or the bus is flooded with unwanted telegrams crossing line boundaries.',
    symptoms: [
      'Devices on the sub-line are reachable within their own line but not from the main line',
      'ETS can download to sub-line devices only when connected directly on that line',
      'Specific group addresses work within one line but not across the coupler',
      'Bus is abnormally loaded with repeated or looping telegrams',
      'Line coupler programming LED will not light when its programming button is pressed',
    ],
    causes: [
      'Filter table has not been programmed — the default state may block all telegrams',
      'Filter table is configured to block the specific GAs needed across the coupler',
      'Bus power is absent on one side of the coupler (main line or sub-line)',
      'Line coupler physical address has not been assigned or conflicts with another device',
      'Coupler is configured in the wrong operating mode (e.g. set as area coupler when used as a line coupler)',
    ],
    steps: [
      {
        title: 'Verify bus voltage on both sides of the coupler',
        detail: 'A KNX line coupler has two bus ports — one connected to the main line and one to the sub-line. Using a multimeter, measure 29 V DC at each port independently. The coupler requires bus power on both sides to operate. A reading of 0 V on either side will prevent telegram routing through the device.',
      },
      {
        title: 'Confirm the coupler has a physical address in ETS',
        detail: 'In ETS Topology, the line coupler should appear as device .0 on the sub-line (e.g. 1.2.0 for sub-line 2 on area 1). Press the programming button on the coupler — the LED should light red. If the PA has never been assigned, the coupler runs a default filter table which may block all GAs.',
      },
      {
        title: 'Check the ETS filter table for the coupler',
        detail: 'Select the coupler in ETS Topology and open its Properties → Filter Table. Each entry shows a GA range and whether it is set to Route (allow) or Block. If the required GAs are set to Block, change them to Route. An empty or unprogrammed filter table defaults to blocking all group telegrams in many coupler implementations.',
      },
      {
        title: 'Download the filter table to the coupler',
        detail: "After editing the filter table in ETS, right-click the coupler and select \"Download → Filter Table\" (or \"Download → All\"). The filter table is stored in the coupler's non-volatile memory. Changes made in ETS do not take effect until downloaded — a common oversight when commissioning multiple couplers.",
      },
      {
        title: 'Confirm the coupler operating mode is correct',
        detail: "KNX couplers are configured as either line couplers (connecting a main line to a sub-line) or area couplers (connecting the area backbone to a main line). In ETS, check the coupler's Application parameter — the topology position in ETS must match the physical installation. An area coupler used as a line coupler will exhibit incorrect routing behaviour.",
      },
      {
        title: 'Monitor telegrams on both sides of the coupler',
        detail: 'Connect ETS to the main line and open Bus Monitor. Trigger a telegram from a sub-line device. If the telegram does not appear on the main line, the filter table is blocking it. Then connect directly to the sub-line side and repeat. This localises whether the fault is on the main line side, the coupler, or the sub-line.',
      },
      {
        title: 'Check for duplicate physical addresses',
        detail: 'Two devices sharing the same PA will cause address conflicts that manifest as intermittent communication failures, especially on line couplers at address .0. In ETS Bus Monitor, look for "ACK Conflict" or "Bus Collision" entries. Use ETS "Bus → Determine physical address" to identify which device responds to the conflicting PA.',
      },
      {
        title: 'Reset and recommission the coupler',
        detail: 'If the coupler behaves erratically after all checks, perform a factory reset (hold the programming button until the LED flashes rapidly — consult the datasheet). Then re-download the PA and filter table from ETS. A corrupted internal state can sometimes persist through configuration downloads without a full reset.',
      },
    ],
  },
  {
    id: 'ip-gateway',
    icon: '⊕',
    title: 'IP Gateway Problems',
    severity: 'Medium',
    overview: 'The KNXnet/IP router or gateway cannot be found by ETS, a visualisation platform, or remote clients — preventing programming over IP, remote monitoring, and app-based control.',
    symptoms: [
      'ETS interface scan returns no results or times out when searching for IP interfaces',
      'Visualisation software (e.g. Gira X1, Loxone) cannot establish a connection to the bus',
      'The gateway web configuration interface does not load in a browser',
      'Remote clients connecting via VPN cannot reach the KNX bus',
      'Only one user can connect at a time and additional connections are refused',
    ],
    causes: [
      'Gateway IP address has changed because it is set to DHCP and the router reassigned the lease',
      'UDP port 3671 is blocked by Windows Firewall, a network firewall, or a NAT rule on the router',
      'Managed switch is filtering multicast packets, preventing KNXnet/IP discovery broadcasts',
      'Gateway is in tunnelling mode, which limits simultaneous connections to 1–4 clients',
      'KNX bus power is absent on the TP side — the gateway cannot route to the bus',
      'Firmware is outdated and incompatible with the installed ETS version or client app',
    ],
    steps: [
      {
        title: "Find the gateway's current IP address",
        detail: "Log into your network router or switch and check the DHCP lease table for the gateway's MAC address. Alternatively, use a network scanner (e.g. Fing or Advanced IP Scanner) to discover all devices on the subnet. Note the current IP — if it differs from what ETS expects, the gateway has been given a new DHCP lease.",
      },
      {
        title: 'Assign a static IP address to the gateway',
        detail: "Access the gateway's web configuration interface (http://<current IP>) and navigate to its network settings. Set a static IP address outside the DHCP pool range (e.g. 192.168.1.200/24, GW: 192.168.1.1). Update the IP address in ETS Settings → Interfaces to match. A static IP prevents future lease changes from breaking the connection.",
      },
      {
        title: 'Ping the gateway from the PC',
        detail: 'Open Command Prompt and run: ping <gateway IP>. Four successful replies confirm basic Layer 3 IP connectivity. If ping fails, check physical cabling (try swapping the Ethernet cable), verify the switch port is active, and confirm the PC and gateway are on the same VLAN — inter-VLAN routing is required if they are separated.',
      },
      {
        title: 'Open the gateway web interface and check status',
        detail: "In a browser on the same network, navigate to http://<gateway IP>. The web interface should show bus connection status, connected clients, and firmware version. A page that fails to load despite a successful ping suggests the gateway's HTTP service has crashed — power-cycle the device to restart all services.",
      },
      {
        title: 'Check firewall rules for UDP port 3671',
        detail: 'KNXnet/IP uses UDP port 3671 for all communication. Open Windows Defender Firewall → Advanced Settings and look for any rules blocking UDP 3671 inbound or outbound. If no explicit rule exists, create an inbound rule: Protocol UDP, Local port 3671, Action Allow. For hardware firewalls, add an equivalent pass rule on the LAN interface.',
      },
      {
        title: 'Check multicast filtering on managed switches',
        detail: 'KNXnet/IP routing uses the multicast address 224.0.23.12. If your LAN uses managed switches with IGMP snooping enabled, multicast may be silently filtered if no IGMP querier is present on the network. Disable IGMP snooping on the port connecting the KNX gateway, or configure a static multicast group entry for 224.0.23.12 on the relevant switch ports.',
      },
      {
        title: 'Switch from tunnelling mode to routing mode for multiple clients',
        detail: 'A KNXnet/IP gateway in tunnelling mode supports a small number of simultaneous connections (typically 1–4 tunnelling channels). If ETS, a visualisation server, and a mobile app all need concurrent access, switch the gateway to routing mode in its web interface or ETS configuration. Routing mode uses multicast and supports unlimited simultaneous clients.',
      },
      {
        title: 'Configure VPN before connecting remotely',
        detail: "ETS and most KNX visualisation apps do not support direct internet-facing KNX connections. For remote access, establish a VPN tunnel to the local network first (e.g. using the router's built-in VPN server or a dedicated VPN gateway). Once the VPN is active, the remote PC should be able to ping the KNX gateway and connect ETS as if it were on the local LAN.",
      },
      {
        title: 'Update the gateway firmware',
        detail: "Download the latest firmware from the manufacturer's support pages (e.g. Gira, ABB, Siemens). Access the gateway web interface → Firmware Update and upload the file. Outdated firmware is a common cause of incompatibility with current ETS6 releases and modern visualisation platforms. Always back up the gateway configuration before updating.",
      },
    ],
  },
];

// ── Sub-components ─────────────────────────────────────────────────────────

function SeverityBadge({ severity, small }) {
  const s = SEVERITY_STYLES[severity] || SEVERITY_STYLES.Medium;
  return (
    <span style={{
      fontSize: small ? 10 : 11, fontWeight: 700,
      padding: small ? '1px 6px' : '2px 9px',
      borderRadius: 4,
      background: s.bg, border: `1px solid ${s.border}`, color: s.text,
      letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>
      {severity}
    </span>
  );
}

function IssueCard({ issue, isSelected, completedCount, onClick }) {
  const [hovered, setHovered] = useState(false);
  const pct = Math.round((completedCount / issue.steps.length) * 100);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%', textAlign: 'left',
        background: isSelected ? C.navy : hovered ? C.navyLight : 'transparent',
        border: 'none',
        borderLeft: `3px solid ${isSelected ? C.orange : 'transparent'}`,
        padding: '11px 14px',
        cursor: 'pointer',
        transition: 'background 0.12s, border-color 0.12s',
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}
    >
      {/* Icon badge */}
      <div style={{
        width: 34, height: 34, borderRadius: 8, flexShrink: 0,
        background: isSelected ? `${C.orange}22` : `${C.navyBorder}88`,
        border: `1.5px solid ${isSelected ? C.orange + '66' : C.navyBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15, color: isSelected ? C.orange : C.slate,
        transition: 'all 0.12s',
      }}>
        {issue.icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12.5, fontWeight: 600,
          color: isSelected ? C.white : C.slateLight,
          lineHeight: 1.3, marginBottom: 4,
        }}>
          {issue.title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <SeverityBadge severity={issue.severity} small />
          {completedCount > 0 && (
            <span style={{ fontSize: 10, color: C.green, fontWeight: 600 }}>
              {completedCount}/{issue.steps.length} done
            </span>
          )}
        </div>
        {/* Progress bar */}
        {completedCount > 0 && (
          <div style={{ marginTop: 5, height: 2, background: C.navyBorder, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              width: `${pct}%`, height: '100%',
              background: pct === 100 ? C.green : C.orange,
              borderRadius: 2, transition: 'width 0.2s',
            }} />
          </div>
        )}
      </div>
    </button>
  );
}

function StepItem({ step, index, isChecked, onToggle }) {
  return (
    <div style={{
      display: 'flex', gap: 13,
      padding: '12px 0',
      borderBottom: `1px solid ${C.navyBorder}`,
      opacity: isChecked ? 0.55 : 1,
      transition: 'opacity 0.15s',
    }}>
      <button
        onClick={onToggle}
        title={isChecked ? 'Mark as not done' : 'Mark as done'}
        style={{
          flexShrink: 0,
          width: 28, height: 28, borderRadius: '50%',
          background: isChecked ? C.green : 'transparent',
          border: `2px solid ${isChecked ? C.green : C.orange}`,
          color: isChecked ? C.white : C.orange,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: isChecked ? 13 : 11, fontWeight: 700,
          transition: 'all 0.15s',
          marginTop: 1,
        }}
      >
        {isChecked ? '✓' : index + 1}
      </button>

      <div style={{ flex: 1 }}>
        <div style={{
          fontWeight: 600, fontSize: 13.5, lineHeight: 1.3, marginBottom: 5,
          color: isChecked ? C.slate : C.white,
          textDecoration: isChecked ? 'line-through' : 'none',
          transition: 'color 0.15s',
        }}>
          {step.title}
        </div>
        <p style={{ margin: 0, color: isChecked ? C.slate : C.slateLight, fontSize: 12.5, lineHeight: 1.65 }}>
          {step.detail}
        </p>
      </div>
    </div>
  );
}

function IssueDetail({ issue, checked, onToggle, onClearProgress }) {
  const doneCount = checked.size;
  const total = issue.steps.length;

  return (
    <div style={{ padding: '1.5rem', overflowY: 'auto' }}>
      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
          background: `${C.orange}22`, border: `2px solid ${C.orange}55`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, color: C.orange,
        }}>
          {issue.icon}
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 19, fontWeight: 700, letterSpacing: '-0.3px' }}>
            {issue.title}
          </h2>
          <SeverityBadge severity={issue.severity} />
        </div>
      </div>

      {/* Overview */}
      <div style={{
        background: `${C.orange}0f`, border: `1.5px solid ${C.orange}44`,
        borderRadius: 9, padding: '12px 14px', marginBottom: '1.25rem',
      }}>
        <div style={{
          fontSize: 10, color: C.orange, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
        }}>
          Overview
        </div>
        <p style={{ margin: 0, color: C.slateLighter, fontSize: 13.5, lineHeight: 1.65 }}>
          {issue.overview}
        </p>
      </div>

      {/* Symptoms + Causes grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem', marginBottom: '1.25rem' }}>
        {/* Symptoms */}
        <div style={{
          background: '#ef444410', border: '1px solid #ef444430',
          borderRadius: 9, padding: '12px 14px',
        }}>
          <div style={{
            fontSize: 10, color: '#f87171', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
          }}>
            ⚠ Symptoms
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {issue.symptoms.map((s, i) => (
              <li key={i} style={{ display: 'flex', gap: 7, fontSize: 12.5, color: C.slateLight, lineHeight: 1.45 }}>
                <span style={{ color: '#f87171', flexShrink: 0, marginTop: 1 }}>▪</span>
                {s}
              </li>
            ))}
          </ul>
        </div>

        {/* Causes */}
        <div style={{
          background: `${C.navyLight}55`, border: `1px solid ${C.navyBorder}`,
          borderRadius: 9, padding: '12px 14px',
        }}>
          <div style={{
            fontSize: 10, color: C.amber, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8,
          }}>
            ◈ Possible Causes
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {issue.causes.map((c, i) => (
              <li key={i} style={{ display: 'flex', gap: 7, fontSize: 12.5, color: C.slateLight, lineHeight: 1.45 }}>
                <span style={{ color: C.amber, flexShrink: 0, marginTop: 1 }}>▪</span>
                {c}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Diagnostic steps */}
      <div style={{
        background: C.navyMid, border: `1.5px solid ${C.navyBorder}`,
        borderRadius: 9, padding: '14px 16px',
      }}>
        {/* Steps header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <div>
            <div style={{
              fontSize: 10, color: C.orange, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4,
            }}>
              Diagnostic Steps
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 120, height: 4, background: C.navyBorder, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  width: `${(doneCount / total) * 100}%`, height: '100%',
                  background: doneCount === total ? C.green : C.orange,
                  borderRadius: 2, transition: 'width 0.2s',
                }} />
              </div>
              <span style={{ fontSize: 11, color: doneCount === total ? C.green : C.slate, fontWeight: 600 }}>
                {doneCount === total && doneCount > 0
                  ? 'All steps complete ✓'
                  : `${doneCount} / ${total} checked`}
              </span>
            </div>
          </div>
          {doneCount > 0 && (
            <button
              onClick={onClearProgress}
              style={{
                background: 'none', border: `1px solid ${C.navyBorder}`,
                color: C.slate, fontSize: 11, cursor: 'pointer',
                borderRadius: 5, padding: '3px 10px',
              }}
            >
              Reset
            </button>
          )}
        </div>

        <p style={{ margin: '0 0 6px', color: C.slate, fontSize: 12 }}>
          Click the step number to mark it as complete and track your diagnostic progress.
        </p>

        {/* Steps list */}
        {issue.steps.map((step, i) => (
          <StepItem
            key={i}
            step={step}
            index={i}
            isChecked={checked.has(i)}
            onToggle={() => onToggle(i)}
          />
        ))}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '3rem', color: C.slate, textAlign: 'center',
    }}>
      <div style={{ fontSize: 40, marginBottom: '1rem', opacity: 0.4 }}>◈</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.slateLight, marginBottom: 6 }}>
        Select an issue to begin
      </div>
      <div style={{ fontSize: 13, maxWidth: 280, lineHeight: 1.6 }}>
        Choose a fault category from the list to view symptoms, causes, and step-by-step diagnostic instructions.
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function KNXTroubleshootingGuide() {
  const [selectedId, setSelectedId]   = useState(null);
  const [checkedSteps, setCheckedSteps] = useState({}); // { [issueId]: Set<number> }

  const selectedIssue = ISSUES.find(i => i.id === selectedId) || null;
  const checked = selectedId ? (checkedSteps[selectedId] || new Set()) : new Set();

  function toggleStep(issueId, stepIdx) {
    setCheckedSteps(prev => {
      const current = new Set(prev[issueId] || []);
      if (current.has(stepIdx)) current.delete(stepIdx);
      else current.add(stepIdx);
      return { ...prev, [issueId]: new Set(current) };
    });
  }

  function clearProgress(issueId) {
    setCheckedSteps(prev => ({ ...prev, [issueId]: new Set() }));
  }

  function completedCount(issueId) {
    return (checkedSteps[issueId] || new Set()).size;
  }

  return (
    <div style={{
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      background: C.navy,
      minHeight: '100vh',
      color: C.white,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* ── Header ── */}
      <div style={{
        background: C.navyMid,
        borderBottom: `3px solid ${C.orange}`,
        padding: '0.875rem 1.75rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            background: C.orange, color: C.white, fontWeight: 800,
            fontSize: 17, padding: '4px 10px', borderRadius: 6, letterSpacing: '-0.5px',
          }}>
            BB
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
              Briq-n-Braq
            </div>
            <div style={{ color: C.slate, fontSize: 11, marginTop: 1 }}>
              KNX Troubleshooting Guide
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: C.orange, fontWeight: 600, fontSize: 12.5 }}>
            KNX Partner No. 20248
          </div>
          <div style={{ color: C.slate, fontSize: 11, marginTop: 2 }}>
            ETS6 · KNX TP · KNXnet/IP
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

        {/* Left: issue list */}
        <div style={{
          width: 270, flexShrink: 0,
          background: C.navyMid,
          borderRight: `1px solid ${C.navyBorder}`,
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            padding: '12px 14px 8px',
            borderBottom: `1px solid ${C.navyBorder}`,
          }}>
            <div style={{ fontSize: 10, color: C.slate, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>
              Fault Categories
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.white }}>
              {ISSUES.length} common KNX issues
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {ISSUES.map(issue => (
              <IssueCard
                key={issue.id}
                issue={issue}
                isSelected={selectedId === issue.id}
                completedCount={completedCount(issue.id)}
                onClick={() => setSelectedId(issue.id)}
              />
            ))}
          </div>

          {/* Severity legend */}
          <div style={{
            padding: '10px 14px',
            borderTop: `1px solid ${C.navyBorder}`,
          }}>
            <div style={{ fontSize: 9.5, color: C.slate, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
              Severity
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.entries(SEVERITY_STYLES).map(([level, s]) => (
                <div key={level} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: C.slate }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: s.text, flexShrink: 0,
                  }} />
                  {level}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: detail */}
        <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
          {selectedIssue ? (
            <IssueDetail
              issue={selectedIssue}
              checked={checked}
              onToggle={(idx) => toggleStep(selectedId, idx)}
              onClearProgress={() => clearProgress(selectedId)}
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{
        padding: '0.75rem 1.75rem',
        background: C.navyMid,
        borderTop: `2px solid ${C.navyBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 11.5, color: C.slate, flexShrink: 0, flexWrap: 'wrap', gap: 8,
      }}>
        <div>
          <span style={{ color: C.white, fontWeight: 600 }}>Briq-n-Braq</span>
          {' · '}KNX Certified Partner No.{' '}
          <span style={{ color: C.orange, fontWeight: 600 }}>20248</span>
        </div>
        <div>ETS6 · KNX TP · KNXnet/IP · IEC 14543-3</div>
      </div>
    </div>
  );
}
